// ============================================================================
// EquipesPage — liste des equipes (E1b, F-206..F-212) + DEFERES
// ============================================================================
// Portage du PoC (dashboard)/equipes/page.tsx au standard maison :
//   - onglets Apercu / Forces & profils (F-202/203, deep-link ?tab=forces) ;
//   - Apercu management : DataTable + KPIs + distinctions (M-200) ;
//   - Apercu employee : cartes identite non cliquables (F-204/205, M-201) ;
//   - Forces : agregats k-anonymat ouverts a tous les roles GEST.
// Regle #4 : zero emoji.

import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, StatusBadge, KpiCard, DataTable, GsProgressBar,
  GsSegmented, SkeletonCard, QueryError, GS_COMPONENTS, type Column,
} from '@gs/ui';
import type { GsSegmentedOption } from '@gs/ui';
import { Users, Smile, Activity, Target, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useMe, isManagementMe } from '../../hooks/useMe';
import {
  useTeams, useTeamsIdentity, useProfilesAnalysis, type TeamSummary,
} from '../../hooks/useTeams';
import { TeamBadgesAside } from '../../components/equipes/TeamBadgesAside';
import { ForcesProfilsPanel } from '../../components/equipes/ForcesProfilsPanel';
import { TeamIdentityCard } from '../../components/equipes/TeamIdentityCard';
import { monthShort } from './dates';

/** Ecran de refus management (M-200/M-402) — l'autorite est le 403 serveur. */
export function TeamsForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:equipes.forbidden.title')}
      description={t('gest:equipes.forbidden.description')}
    />
  );
}

/** Moyenne pulse formatee locale (« 3,8 » / « 3.8 ») — 1 decimale. */
function formatMood(locale: string, value: number): string {
  return new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  }).format(value);
}

export default function EquipesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const meQ = useMe(!!user);
  const isManagement = isManagementMe(meQ.data);

  const tab = searchParams.get('tab') === 'forces' ? 'forces' : 'apercu';

  const listQ = useTeams(!!user && isManagement && tab === 'apercu');
  const identityQ = useTeamsIdentity(!!user && !isManagement && tab === 'apercu' && !!meQ.data);
  const analysisQ = useProfilesAnalysis(!!user && tab === 'forces');

  const tabOptions: GsSegmentedOption[] = useMemo(() => [
    { value: 'apercu', label: t('gest:equipes.tabs.apercu') },
    { value: 'forces', label: t('gest:equipes.tabs.forces') },
  ], [t]);

  const setTab = (value: string) => {
    if (value === 'forces') setSearchParams({ tab: 'forces' });
    else setSearchParams({});
  };

  // ─── Colonnes DataTable (nom, responsable, statut, membres, etat pulse, objectifs) ──
  const columns: Column<TeamSummary>[] = [
    {
      key: 'name',
      header: t('gest:equipes.columns.name'),
      render: (team) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-900 dark:text-slate-100">{team.name}</p>
          {team.department ? (
            <p className="truncate text-meta text-slate-500 dark:text-slate-400">{team.department}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'director',
      header: t('gest:equipes.columns.director'),
      render: (team) =>
        team.directorName ?? team.directorEmail ?? (
          <span className="text-slate-400 dark:text-slate-500">{t('gest:equipes.noDirector')}</span>
        ),
    },
    {
      key: 'status',
      header: t('gest:equipes.columns.status'),
      render: (team) =>
        team.submittedThisPeriod ? (
          <StatusBadge variant="success">{t('gest:equipes.pulse.submitted')}</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">{t('gest:equipes.pulse.pending')}</StatusBadge>
        ),
    },
    {
      key: 'members',
      header: t('gest:equipes.columns.members'),
      className: 'tabular-nums',
      render: (team) => team.memberCount || team.headcount,
    },
    {
      key: 'pulse',
      header: t('gest:equipes.columns.pulse'),
      render: (team) => {
        if (team.submittedThisPeriod && team.currentMood != null) {
          return <span className="font-semibold tabular-nums">{t('gest:equipes.pulse.score', { score: team.currentMood })}</span>;
        }
        if (team.latestMood != null && team.latestPeriod) {
          return (
            <span className="text-meta text-slate-500 dark:text-slate-400">
              {t('gest:equipes.pulse.lastSeen', {
                month: monthShort(i18n.language, team.latestPeriod),
                score: team.latestMood,
              })}
            </span>
          );
        }
        return <span className="text-slate-400 dark:text-slate-500">{t('gest:equipes.pulse.none')}</span>;
      },
    },
    {
      key: 'objectives',
      header: t('gest:equipes.columns.objectives'),
      render: (team) =>
        team.objectivesTotal > 0 ? (
          <GsProgressBar value={team.objectivesDone} max={team.objectivesTotal} label className="w-32" />
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        ),
    },
  ];

  const forbidden =
    (listQ.isError && listQ.error instanceof ApiForbiddenError)
    || (identityQ.isError && identityQ.error instanceof ApiForbiddenError)
    || (analysisQ.isError && analysisQ.error instanceof ApiForbiddenError);

  const refresh = () => {
    if (tab === 'forces') void analysisQ.refetch();
    else if (isManagement) void listQ.refetch();
    else void identityQ.refetch();
  };
  const refreshLoading =
    tab === 'forces' ? analysisQ.isFetching
      : isManagement ? listQ.isFetching
        : identityQ.isFetching;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:equipes.title')}
        onRefresh={refresh}
        refreshLoading={refreshLoading}
      />

      <GsSegmented
        options={tabOptions}
        value={tab}
        onChange={setTab}
        ariaLabel={t('gest:equipes.tabs.aria')}
      />

      {forbidden ? (
        <TeamsForbidden />
      ) : tab === 'forces' ? (
        analysisQ.isLoading || meQ.isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : analysisQ.isError ? (
          <QueryError message={t('gest:equipes.loadError')} onRetry={() => void analysisQ.refetch()} />
        ) : analysisQ.data ? (
          <ForcesProfilsPanel analysis={analysisQ.data.analysis} />
        ) : null
      ) : isManagement ? (
        listQ.isLoading || meQ.isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : listQ.isError ? (
          <QueryError message={t('gest:equipes.loadError')} onRetry={() => void listQ.refetch()} />
        ) : listQ.data ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                icon={Users}
                variant="brand"
                label={t('gest:equipes.kpis.teams')}
                value={t('gest:equipes.kpis.teamsValue', { teams: listQ.data.kpis.teamCount, people: listQ.data.kpis.totalHeadcount })}
              />
              <KpiCard
                icon={Smile}
                variant="green"
                label={t('gest:equipes.kpis.mood')}
                value={
                  listQ.data.kpis.moodAvg != null
                    ? t('gest:equipes.pulse.score', { score: formatMood(i18n.language, listQ.data.kpis.moodAvg) })
                    : '—'
                }
              />
              <KpiCard
                icon={Activity}
                variant="blue"
                label={t('gest:equipes.kpis.submitted')}
                value={`${listQ.data.kpis.submittedCount}/${listQ.data.kpis.teamCount}`}
              />
              <KpiCard
                icon={Target}
                variant="brand"
                label={t('gest:equipes.kpis.objectives')}
                value={`${listQ.data.kpis.objectivesDone}/${listQ.data.kpis.objectivesTotal}`}
              />
            </div>

            {listQ.data.teams.length === 0 ? (
              <EmptyState icon={<Users size={48} aria-hidden="true" />} title={t('gest:equipes.empty')} />
            ) : (
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_20rem]">
                <div className={`${GS_COMPONENTS.card} overflow-hidden p-0`}>
                  <DataTable
                    columns={columns}
                    data={listQ.data.teams}
                    rowKey={(team) => team.id}
                    onRowClick={(team) => navigate(`/equipes/${team.id}`)}
                  />
                </div>
                <TeamBadgesAside teams={listQ.data.teams} currentPeriod={listQ.data.badgePeriod} />
              </div>
            )}
          </>
        ) : null
      ) : identityQ.isLoading || meQ.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : identityQ.isError ? (
        <QueryError message={t('gest:equipes.loadError')} onRetry={() => void identityQ.refetch()} />
      ) : identityQ.data ? (
        identityQ.data.teams.length === 0 ? (
          <EmptyState icon={<Users size={48} aria-hidden="true" />} title={t('gest:equipes.empty')} />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {identityQ.data.teams.map((team) => (
              <TeamIdentityCard key={team.id} team={team} />
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}
